import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import {
  Radio,
  At,
  Lock,
  User,
  UserCircle,
  CheckCircle,
  XCircle,
  Eye,
  EyeSlash,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const registerTexts = {
  de: {
    title: "Konto erstellen",
    subtitle: "Werde Teil der globalen Radio-Community",
    fullName: "Vollständiger Name",
    username: "Benutzername",
    email: "E-Mail",
    password: "Passwort",
    usernameHint:
      "3–24 Zeichen, nur Kleinbuchstaben, Zahlen und Unterstrich.",
    ruleLength: "Mindestens 8 Zeichen",
    ruleUppercase: "Ein Großbuchstabe",
    ruleNumber: "Eine Zahl",
    ruleSpecial: "Ein Sonderzeichen",
    createAccount: "Konto erstellen",
    creatingAccount: "Konto wird erstellt...",
    alreadyHaveAccount: "Du hast bereits ein Konto?",
    login: "Einloggen",
    backToHome: "Zurück zur Startseite",
    missingFields: "Bitte alle Felder ausfüllen.",
    invalidUsername:
      "Benutzername: 3–24 Zeichen, nur Kleinbuchstaben, Zahlen und Unterstrich.",
    weakPassword:
      "Passwort zu schwach. Bitte alle Passwort-Regeln erfüllen.",
    registerFailed: "Registrierung fehlgeschlagen.",
    hidePassword: "Passwort ausblenden",
    showPassword: "Passwort anzeigen",
  },

  en: {
    title: "Create an account",
    subtitle: "Join the global radio community",
    fullName: "Full Name",
    username: "Username",
    email: "Email",
    password: "Password",
    usernameHint:
      "3–24 characters, lowercase letters, numbers and underscore only.",
    ruleLength: "At least 8 characters",
    ruleUppercase: "One uppercase letter",
    ruleNumber: "One number",
    ruleSpecial: "One special character",
    createAccount: "Create account",
    creatingAccount: "Creating account...",
    alreadyHaveAccount: "Already have an account?",
    login: "Log in",
    backToHome: "Back to home",
    missingFields: "Please fill in all fields.",
    invalidUsername:
      "Username: 3–24 characters, lowercase letters, numbers and underscore only.",
    weakPassword:
      "Password too weak. Please meet all password rules.",
    registerFailed: "Registration failed.",
    hidePassword: "Hide password",
    showPassword: "Show password",
  },

  no: {
    title: "Opprett konto",
    subtitle: "Bli med i det globale radiofellesskapet",
    fullName: "Fullt navn",
    username: "Brukernavn",
    email: "E-post",
    password: "Passord",
    usernameHint:
      "3–24 tegn, kun små bokstaver, tall og understrek.",
    ruleLength: "Minst 8 tegn",
    ruleUppercase: "Én stor bokstav",
    ruleNumber: "Ett tall",
    ruleSpecial: "Ett spesialtegn",
    createAccount: "Opprett konto",
    creatingAccount: "Oppretter konto...",
    alreadyHaveAccount: "Har du allerede en konto?",
    login: "Logg inn",
    backToHome: "Tilbake til startsiden",
    missingFields: "Fyll ut alle feltene.",
    invalidUsername:
      "Brukernavn: 3–24 tegn, kun små bokstaver, tall og understrek.",
    weakPassword:
      "Passordet er for svakt. Oppfyll alle passordreglene.",
    registerFailed: "Registrering mislyktes.",
    hidePassword: "Skjul passord",
    showPassword: "Vis passord",
  },

  ko: {
    title: "계정 만들기",
    subtitle: "글로벌 라디오 커뮤니티에 참여하세요",
    fullName: "전체 이름",
    username: "사용자 이름",
    email: "이메일",
    password: "비밀번호",
    usernameHint:
      "3–24자, 소문자, 숫자, 밑줄만 사용할 수 있습니다.",
    ruleLength: "최소 8자",
    ruleUppercase: "대문자 1개",
    ruleNumber: "숫자 1개",
    ruleSpecial: "특수문자 1개",
    createAccount: "계정 만들기",
    creatingAccount: "계정 생성 중...",
    alreadyHaveAccount: "이미 계정이 있으신가요?",
    login: "로그인",
    backToHome: "홈으로 돌아가기",
    missingFields: "모든 필드를 입력하세요.",
    invalidUsername:
      "사용자 이름: 3–24자, 소문자, 숫자, 밑줄만 사용할 수 있습니다.",
    weakPassword:
      "비밀번호가 너무 약합니다. 모든 비밀번호 규칙을 충족하세요.",
    registerFailed: "가입에 실패했습니다.",
    hidePassword: "비밀번호 숨기기",
    showPassword: "비밀번호 보기",
  },

  fi: {
    title: "Luo tili",
    subtitle: "Liity maailmanlaajuiseen radioyhteisöön",
    fullName: "Koko nimi",
    username: "Käyttäjänimi",
    email: "Sähköposti",
    password: "Salasana",
    usernameHint:
      "3–24 merkkiä, vain pienet kirjaimet, numerot ja alaviiva.",
    ruleLength: "Vähintään 8 merkkiä",
    ruleUppercase: "Yksi iso kirjain",
    ruleNumber: "Yksi numero",
    ruleSpecial: "Yksi erikoismerkki",
    createAccount: "Luo tili",
    creatingAccount: "Luodaan tiliä...",
    alreadyHaveAccount: "Onko sinulla jo tili?",
    login: "Kirjaudu sisään",
    backToHome: "Takaisin etusivulle",
    missingFields: "Täytä kaikki kentät.",
    invalidUsername:
      "Käyttäjänimi: 3–24 merkkiä, vain pienet kirjaimet, numerot ja alaviiva.",
    weakPassword:
      "Salasana on liian heikko. Täytä kaikki salasanavaatimukset.",
    registerFailed: "Rekisteröinti epäonnistui.",
    hidePassword: "Piilota salasana",
    showPassword: "Näytä salasana",
  },
};

const Register = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    username: "",
  });

  const [error, setError] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const { register, user, loading } = useAuth();
  const { language } = useLanguage();

  const text = registerTexts[language] || registerTexts.en;

  useEffect(() => {
    if (!loading && user) {
      navigate("/discover", { replace: true });
    }
  }, [user, loading, navigate]);

  const passwordRules = useMemo(() => {
    const password = formData.password;

    return [
      {
        label: text.ruleLength,
        valid: password.length >= 8,
      },
      {
        label: text.ruleUppercase,
        valid: /[A-Z]/.test(password),
      },
      {
        label: text.ruleNumber,
        valid: /[0-9]/.test(password),
      },
      {
        label: text.ruleSpecial,
        valid: /[^A-Za-z0-9]/.test(password),
      },
    ];
  }, [formData.password, text]);

  const isPasswordStrong = passwordRules.every((rule) => rule.valid);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((currentData) => ({
      ...currentData,
      [name]: name === "username" ? value.toLowerCase() : value,
    }));

    if (error) {
      setError("");
    }
  };

  const validateForm = () => {
    const cleanEmail = formData.email.trim().toLowerCase();
    const cleanName = formData.name.trim();
    const cleanUsername = formData.username.trim().toLowerCase();

    if (!cleanName || !cleanUsername || !cleanEmail || !formData.password) {
      return text.missingFields;
    }

    if (!/^[a-z0-9_]{3,24}$/.test(cleanUsername)) {
      return text.invalidUsername;
    }

    if (!isPasswordStrong) {
      return text.weakPassword;
    }

    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    const cleanEmail = formData.email.trim().toLowerCase();
    const cleanName = formData.name.trim();
    const cleanUsername = formData.username.trim().toLowerCase();

    setError("");
    setSubmitLoading(true);

    const result = await register(
      cleanEmail,
      formData.password,
      cleanName,
      cleanUsername
    );

    setSubmitLoading(false);

    if (result.success) {
      navigate("/discover", { replace: true });
      return;
    }

    setError(result.error || text.registerFailed);
  };

  const isButtonDisabled = submitLoading || loading;

  const PasswordRule = ({ valid, label }) => {
    return (
      <div
        className={`flex items-center gap-2 text-xs ${valid ? "text-cyan-300" : "text-gray-500"
          }`}
      >
        {valid ? (
          <CheckCircle size={15} weight="fill" className="text-cyan-400" />
        ) : (
          <XCircle size={15} className="text-gray-600" />
        )}
        <span>{label}</span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <Radio size={40} weight="duotone" className="text-cyan-400" />
          <span
            className="text-3xl font-black tracking-tighter text-white"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            WORLDofRADIO
          </span>
        </div>

        <div className="backdrop-filter backdrop-blur-2xl bg-black/60 border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)] rounded-3xl p-8">
          <h1
            className="text-2xl sm:text-3xl tracking-tight font-bold mb-2 text-white"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {text.title}
          </h1>

          <p className="text-sm text-gray-400 mb-8">{text.subtitle}</p>

          {error && (
            <div
              data-testid="register-error"
              className="bg-red-500/10 border border-red-500/50 rounded-xl p-4 mb-6 text-sm text-red-400"
              role="alert"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-white mb-2 block">
                {text.fullName}
              </Label>

              <div className="relative">
                <User
                  size={20}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                />

                <Input
                  data-testid="register-name-input"
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="pl-12 bg-[#0A0E17] border-white/10 text-white h-12 rounded-xl focus:border-cyan-500 focus:ring-cyan-500/20"
                  placeholder="John Doe"
                  required
                  disabled={isButtonDisabled}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="username" className="text-white mb-2 block">
                {text.username}
              </Label>

              <div className="relative">
                <UserCircle
                  size={20}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                />

                <Input
                  data-testid="register-username-input"
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  value={formData.username}
                  onChange={handleChange}
                  className="pl-12 bg-[#0A0E17] border-white/10 text-white h-12 rounded-xl focus:border-cyan-500 focus:ring-cyan-500/20"
                  placeholder="johndoe"
                  required
                  disabled={isButtonDisabled}
                />
              </div>

              <p className="text-xs text-gray-500 mt-2">
                {text.usernameHint}
              </p>
            </div>

            <div>
              <Label htmlFor="email" className="text-white mb-2 block">
                {text.email}
              </Label>

              <div className="relative">
                <At
                  size={20}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                />

                <Input
                  data-testid="register-email-input"
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-12 bg-[#0A0E17] border-white/10 text-white h-12 rounded-xl focus:border-cyan-500 focus:ring-cyan-500/20"
                  placeholder="you@example.com"
                  required
                  disabled={isButtonDisabled}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-white mb-2 block">
                {text.password}
              </Label>

              <div className="relative">
                <Lock
                  size={20}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                />

                <Input
                  data-testid="register-password-input"
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-12 pr-12 bg-[#0A0E17] border-white/10 text-white h-12 rounded-xl focus:border-cyan-500 focus:ring-cyan-500/20"
                  placeholder="Test123!"
                  required
                  disabled={isButtonDisabled}
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-cyan-400 transition-colors disabled:opacity-50"
                  aria-label={showPassword ? text.hidePassword : text.showPassword}
                  disabled={isButtonDisabled}
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>

              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-3">
                {passwordRules.map((rule) => (
                  <PasswordRule
                    key={rule.label}
                    valid={rule.valid}
                    label={rule.label}
                  />
                ))}
              </div>
            </div>

            <Button
              data-testid="register-submit-button"
              type="submit"
              disabled={isButtonDisabled}
              className="w-full h-12 bg-cyan-400 hover:bg-cyan-300 text-black font-bold rounded-xl mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitLoading ? text.creatingAccount : text.createAccount}
            </Button>
          </form>

          <p className="text-center text-sm text-gray-400 mt-8">
            {text.alreadyHaveAccount}{" "}
            <Link
              to="/login"
              className="text-cyan-400 hover:text-cyan-300 font-semibold"
            >
              {text.login}
            </Link>
          </p>
        </div>

        <div className="text-center mt-6">
          <Link
            to="/"
            className="text-sm text-gray-400 hover:text-cyan-400 transition-colors"
          >
            ← {text.backToHome}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Register;